<template>
  <b-row>
    <b-col :md="md" cols="12">
      <b-form-group :label="label" :label-for="label">
        <v-select
          :loading="loading"
          :dir="locale === 'ar' ? 'rtl' : 'ltr'"
          :disabled="disabled"
          :multiple="multiple"
          :value="value"
          :required="required"
          :filterable="false"
          :options="transformedItems"
          :reduce="serializer"
          :label="isRelational ? undefined : textTitle"
          :placeholder="placeholder"
          :close-on-select="!multiple"
          @search="searchItem"
          @input="selected"
          attach
          clearable
        >
          <template v-slot:option="option">
            <div class="d-flex justify-content-between align-items-center w-100">
              <span class="font-weight-medium">{{ option[textTitle] || option.label }}</span>
              <b-badge variant="success" style="margin-left: 10px;" class="">{{ formatPrice(option.price || 0) }}</b-badge>
            </div>
          </template>

          <template v-slot:selected-option="option">
            <div class="d-flex justify-content-between align-items-center">
              <span>{{ option[textTitle] || option.label }}</span>
              <b-badge variant="success" s class="ml-2" style="font-size: 0.8rem;margin: 4px;">{{ formatPrice(option.price || 0) }}</b-badge>
            </div>
          </template>

          <template v-slot:no-options="props">
            <slot name="no-option" v-bind="props"></slot>
          </template>
        </v-select>
        <slot name="item-select"></slot>
        <b-form-invalid-feedback :state="!errorMessages">
          <span
            v-for="(err, index) in errorMessages"
            :key="index"
            v-text="err"
          ></span>
        </b-form-invalid-feedback>
      </b-form-group>
    </b-col>
    <b-col :md="md" cols="12">
      <b-form-group :label="$t('common.total-amount')" label-for="amount">
        <b-form-input
          disabled
          id="amount"
          type="text"
          :value="formatPrice(totalPrice)"
          :placeholder="$t('common.amount')"
          class="font-weight-bold text-success"
        />
        <p class="text-danger mt-1">{{ ErrorAmount }}</p>
      </b-form-group>
    </b-col>
  </b-row>
</template>

<script>
import {
  BFormGroup,
  BFormInput,
  BFormInvalidFeedback,
  BRow,
  BCol,
  BBadge,
} from 'bootstrap-vue';
import { debounce } from 'lodash';
import vSelect from 'vue-select';

export default {
  components: {
    vSelect,
    BFormInvalidFeedback,
    BFormGroup,
    BFormInput,
    BRow,
    BCol,
    BBadge,
  },
  props: {
    ErrorAmount: String,
    errorMessages: Array,
    value: [Object, String, Array],
    placeholder: String,
    label: String,
    paramQuery: String,
    serializer: Function,
    required: Boolean,
    multiple: Boolean,
    textTitle: String,
    disabled: Boolean,
    otherQuery: String,
    employeeId: String,
    keyword: String,
    isRelational: Boolean,
    altSearch: String,
    disableAmount: Boolean,
    type: {
      type: String,
      default: 'regular',
    },
    md: {
      type: String,
      default: '12',
    },
  },
  data: () => ({
    items: [],
    loading: false,
    locale: localStorage.getItem('lng'),
    selection: [],
    searchValue: '',
    finalValue: '',
  }),

  watch: {
    otherQuery() {
      if (!this.disabled) {
        this.getItems().then(() => {
          if (this.value) {
            if (this.multiple && Array.isArray(this.value)) {
              // Handle multiple selection
              const valuesToAdd = [];
              this.value.forEach(val => {
                let item = this.items.find((it) => it.id === val.id);
                if (!item) {
                  this.items.push(val);
                  valuesToAdd.push(val);
                } else {
                  valuesToAdd.push(item);
                }
              });
              this.$emit('input', valuesToAdd);
            } else if (this.value.id) {
              // Handle single selection
              let item = this.items.find((it) => it.id === this.value.id);
              if (!item) {
                this.items.push(this.value);
                this.$emit('input', this.value);
              } else {
                this.$emit('input', item);
              }
            }
          }
        });
      }
    },
  },
  computed: {
    transformedItems() {
      if (this.textTitle.includes('.')) {
        const beforeText = this.getWordBeforeDot(this.textTitle);
        const afterText = this.getStringAfterDot(this.textTitle);

        return this.items?.map((item) => {
          const nestedProperty = item[beforeText];
          return {
            ...item,
            label: nestedProperty ? nestedProperty[afterText] : undefined,
          };
        });
      } else {
        return this.items;
      }
    },

    // Calculate total price for selected services
    totalPrice() {
      if (!this.value) return 0;
      
      // Handle multiple selection
      if (this.multiple && Array.isArray(this.value)) {
        return this.value.reduce((total, service) => {
          const price = parseFloat(service?.price || 0);
          return total + price;
        }, 0);
      }
      
      // Handle single selection
      return parseFloat(this.value?.price || 0);
    },
  },
  methods: {
    // Format price with currency
    formatPrice(price) {
      return price ? `${Number(price).toLocaleString()} IQD` : "0 IQD";
    },

    addToItems(item) {
      this.items.push(item);
    },
    getWordBeforeDot(inputString) {
      const dotIndex = inputString.indexOf('.');
      if (dotIndex !== -1) {
        return inputString.substring(0, dotIndex);
      } else {
        return ''; // Return an empty string if no dot is found
      }
    },
    getStringAfterDot(inputString) {
      const dotIndex = inputString.indexOf('.');
      if (dotIndex !== -1) {
        return inputString.substring(dotIndex + 1);
      } else {
        return ''; // Return an empty string if no dot is found
      }
    },
    selected(vals) {
      this.finalValue = vals;
      this.$emit('val', vals);
      this.$emit('input', vals);
      
      // Emit total price
      let calculatedTotal = 0;
      if (this.multiple && Array.isArray(vals)) {
        calculatedTotal = vals.reduce((total, service) => {
          return total + (parseFloat(service?.price || 0));
        }, 0);
      } else if (vals) {
        calculatedTotal = parseFloat(vals?.price || 0);
      }
      this.$emit('total-price', calculatedTotal);
      
      this.$emit('hide', false);
      this.$emit('close', false);
      this.$emit('hidden', false);
    },
    async getItems(query = '', pageSize = 5) {
      this.loading = true;
      let url = `/service?page=1&filter[type]=${this.type}&take=10&sort=-id&${`filter[${
        this.altSearch ? this.altSearch : this.textTitle
      }]=${query ?? ''}`}`;
      if (this.otherQuery) {
        url += `&${this.otherQuery}`;
      }
      if (this.isRelational) {
        url += `&filter[${this.paramQuery}]=${query ?? ''}`;
      }
      try {
        await this.getList(url);
        this.items = this.all_data.data;
      } catch (error) {
        console.error('Error fetching services:', error);
        this.items = [];
      } finally {
        this.loading = false;
      }
    },
    searchItem: debounce(function (query) {
      // INFO: For some reason, when the debounce function is rand after selecting an item...
      // If the item was not returned from the this.getItems() than it'll show the ID of the item instead..
      // of it's name. hence why we are passing a 500 pageSize
      this.getItems(query, 500);
    }, 500),
  },

  mounted() {
    if (!this.disabled) {
      this.getItems().then(() => {
        if (this.value) {
          if (this.multiple && Array.isArray(this.value)) {
            // Handle multiple selection
            const valuesToAdd = [];
            this.value.forEach(val => {
              let item = this.items.find((it) => it.id === val.id);
              if (!item) {
                this.items.push(val);
                valuesToAdd.push(val);
              } else {
                valuesToAdd.push(item);
              }
            });
            this.$emit('input', valuesToAdd);
          } else if (this.value.id) {
            // Handle single selection
            let item = this.items.find((it) => it.id === this.value.id);
            if (!item) {
              this.items.push(this.value);
              this.$emit('input', this.value);
            } else {
              this.$emit('input', item);
            }
          }
        }
      }).catch(error => {
        console.error('Error in mounted hook:', error);
        // Silently handle the error to prevent page crashes
      });
    }
  },
};
</script>
<style scoped>
.v-select-dropdown {
  z-index: 9999999 !important; /* Ensure it's higher than the table's z-index */
}
.dropdown-menu {
  z-index: 1050 !important; /* Use a value high enough to overlay other components */
}

/* Improve spacing and readability for service options */
.v-select ::v-deep .vs__dropdown-option {
  padding: 10px 15px;
}

.v-select ::v-deep .vs__selected {
  margin: 2px 4px;
  padding: 2px 6px;
}
</style>
